'use client';

import { useState, useEffect, FormEvent } from 'react';
import { useAuth, User } from '../context/AuthContext';
import { clienteApi } from '@/services/clienteApi';
import {
    REQUISITOS_DE_CONTRASENA,
    esContrasenaFuerte,
    evaluarRequisitos,
} from '@/lib/security/fuerzaDeContrasena';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { useGoogleLogin, TokenResponse } from '@react-oauth/google';
import { mensajeErrorGoogle } from '@/lib/auth/erroresInicioGoogle';

interface RegisterResponse {
    token?: string;
    tempToken?: string;
    mfaRequired?: boolean;
    usuario: User;
    message?: string;
    code?: string;
}

export default function RegisterPage() {
    const [name, setName] = useState('');
    const [apellidos, setApellidos] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [consentimientoPII, setConsentimientoPII] = useState(false);
    const [consentimientoMarketing, setConsentimientoMarketing] = useState(false);

    // T-1100/T-1101: Analizador de Seguridad.
    // La regla vive en `lib/security/fuerzaDeContrasena`, que replica la del
    // servidor. La copia local que había aquí no comprobaba la minúscula —que
    // el servidor sí exige— y limitaba el símbolo a siete caracteres, así que
    // esta lista podía mostrarse entera en verde y el alta fallar igual.
    const strength = evaluarRequisitos(password);
    const isPasswordValid = esContrasenaFuerte(password);
    // const passwordsMatch = formData.password === formData.confirmPassword; && password !== '';

    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const { login } = useAuth();
    const useRouterInstance = useRouter();

    // T-3011: Capturar cupón de la URL
    const [coupon, setCoupon] = useState<string | null>(null);

    useEffect(() => {
        // Extraer query params manualmente para evitar problemas con useSearchParams en Next 13+ app dir client components a veces
        const params = new URLSearchParams(window.location.search);
        const paramCoupon = params.get('coupon');
        if (paramCoupon) {
            setCoupon(paramCoupon);
        }
    }, []);

    // --- 1. Registro Normal ---
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        if (!consentimientoPII) {
            setError("Debes aceptar el tratamiento de datos de salud (RGPD).");
            setIsLoading(false);
            return;
        }

        if (!isPasswordValid) {
            setError("La contraseña no cumple con los requisitos de seguridad.");
            setIsLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            setError("Las contraseñas no coinciden.");
            setIsLoading(false);
            return;
        }

        try {
            const res = await clienteApi.crudo('/api/auth/register', {
                method: 'POST',
                cuerpo: {
                    nombre: name,
                    apellidos,
                    email,
                    password,
                    consentimientoPII,
                    consentimientoMarketing,
                    coupon // Enviar cupón si existe
                },
            });

            const data: RegisterResponse = await res.json();

            if (res.ok) {
                // Login automático tras registro (Legacy/Fallback)
                login(data.usuario);
                useRouterInstance.push(`/verify-email?email=${encodeURIComponent(email)}`);
            } else {
                setError(data.message || 'Error al registrarse');
            }
        } catch (err) {
            console.error('Register error:', err);
            setError('Error de conexión con el servidor');
        } finally {
            setIsLoading(false);
        }
    };

    // --- 2. Registro con Google ---
    const handleGoogleLogin = useGoogleLogin({
        onSuccess: async (tokenResponse: TokenResponse) => {
            setIsLoading(true);
            setError('');

            try {
                // Enviamos el token al backend
                const res = await clienteApi.crudo('/api/auth/google', {
                    method: 'POST',
                    cuerpo: { token: tokenResponse.access_token },
                });

                const data: RegisterResponse = await res.json();

                if (res.ok) {
                    if (data.mfaRequired) {
                        setError('La cuenta ya existe y requiere MFA. Por favor, inicia sesión.');
                        // Opcionalmente redirigir: useRouterInstance.push('/app/login');
                    } else {
                        login(data.usuario);
                        // Smart redirection based on subscription status
                        if (['active', 'trialing'].includes(data.usuario?.subscriptionStatus)) {
                            useRouterInstance.push('/app/dashboard/patients');
                        } else {
                            useRouterInstance.push('/app/subscribe');
                        }
                    }
                } else {
                    setError(mensajeErrorGoogle(res.status, data, 'Error al registrarse con Google'));
                }
            } catch (err) {
                console.error('Google auth error:', err);
                setError('Error de conexión con el servidor al verificar Google');
            } finally {
                setIsLoading(false);
            }
        },
        onError: () => {
            console.error('Google Register Failed');
            setError('Falló el registro con Google');
        },
    });

    return (
        <div className="flex min-h-screen bg-canvas landing-grid items-center justify-center p-4">

            {/* Tarjeta Horizontal Grande */}
            <div className="w-full max-w-6xl bg-surface-2 rounded-3xl shadow-[12px_12px_0px_0px_var(--shadow-color)] border-2 border-ink flex flex-col md:flex-row overflow-hidden">

                {/* Columna Izquierda: Branding */}
                <div className="w-full md:w-5/12 bg-accent-blue-light/30 p-12 flex flex-col justify-center items-center text-center border-b-2 md:border-b-0 md:border-r-2 border-ink relative">
                    <Link href="/" className="relative w-64 h-64 mb-6 transition-transform hover:scale-105">
                        <Image
                            src="/THERAPY(2).svg"
                            alt="TherapyRecap Logo"
                            fill
                            className="object-contain dark:invert"
                            priority
                        />
                    </Link>
                    <h1 className="text-3xl font-display font-bold text-ink mb-4">
                        Crea tu cuenta
                    </h1>
                    <p className="text-ink/80 text-lg max-w-xs mx-auto font-medium">
                        Únete y optimiza tu práctica hoy mismo.
                    </p>
                </div>

                {/* Columna Derecha: Formulario */}
                <div className="w-full md:w-7/12 p-8 md:p-12 bg-surface-2">
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {error && (
                            <div className="p-3 bg-tint-danger-strong border-2 border-danger text-on-tint-danger text-sm rounded-xl font-bold text-center shadow-[4px_4px_0px_0px_#ef4444]">
                                {error}
                            </div>
                        )}

                        {/* Fila: Nombre y Apellidos */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-xs font-bold text-ink mb-1.5 uppercase tracking-wider">
                                    Nombre
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full bg-surface-2 border-2 border-ink text-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ink/20 focus:border-ink transition-all placeholder:text-ink/40 font-medium"
                                    placeholder="Dr. Juan"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-ink mb-1.5 uppercase tracking-wider">
                                    Apellidos
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={apellidos}
                                    onChange={(e) => setApellidos(e.target.value)}
                                    className="w-full bg-surface-2 border-2 border-ink text-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ink/20 focus:border-ink transition-all placeholder:text-ink/40 font-medium"
                                    placeholder="Pérez"
                                />
                            </div>
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-xs font-bold text-ink mb-1.5 uppercase tracking-wider">
                                Email Profesional
                            </label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-surface-2 border-2 border-ink text-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ink/20 focus:border-ink transition-all placeholder:text-ink/40 font-medium"
                                placeholder="tu@email.com"
                            />
                        </div>

                        {/* Contraseña */}
                        <div>
                            <label className="block text-xs font-bold text-ink mb-1.5 uppercase tracking-wider">
                                Contraseña
                            </label>
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-surface-2 border-2 border-ink text-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ink/20 focus:border-ink transition-all placeholder:text-ink/40 font-medium"
                                placeholder="••••••••"
                            />
                            {/* T-1101: Analizador en Vivo (UI Refinada) */}
                            {/* Se recorre la lista de requisitos en vez de
                                escribirlos a mano: así lo que se muestra no
                                puede volver a desalinearse de lo que se
                                valida. */}
                            <ul className="mt-3 space-y-1">
                                {REQUISITOS_DE_CONTRASENA.map((requisito) => (
                                    <li
                                        key={requisito.id}
                                        className={`flex items-center gap-2 text-xs font-medium ${strength[requisito.id] ? 'text-on-tint-success' : 'text-ink/50'}`}
                                    >
                                        <span className="material-symbols-outlined text-[16px]" aria-hidden="true">
                                            {strength[requisito.id] ? 'check_circle' : 'radio_button_unchecked'}
                                        </span>
                                        {requisito.etiqueta}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Confirmar Contraseña */}
                        <div>
                            <label className="block text-xs font-bold text-ink mb-1.5 uppercase tracking-wider">
                                Confirmar Contraseña
                            </label>
                            <input
                                type="password"
                                required
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className={`w-full bg-surface-2 border-2 text-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-2 transition-all placeholder:text-ink/40 font-medium ${confirmPassword && password !== confirmPassword
                                    ? 'border-danger focus:border-danger focus:ring-danger'
                                    : 'border-ink focus:border-ink focus:ring-ink/20'
                                    }`}
                                placeholder="••••••••"
                            />
                            {confirmPassword && password !== confirmPassword && (
                                <p className="text-xs text-on-tint-danger font-bold mt-1">Las contraseñas no coinciden</p>
                            )}
                        </div>

                        {/* Checkbox RGPD */}
                        <div className="flex items-start gap-3 mt-4">
                            <div className="flex items-center h-5">
                                <input
                                    id="rgpd"
                                    type="checkbox"
                                    checked={consentimientoPII}
                                    onChange={(e) => setConsentimientoPII(e.target.checked)}
                                    className="w-5 h-5 text-ink bg-surface-2 border-2 border-ink rounded focus:ring-ink focus:ring-2 cursor-pointer appearance-none checked:bg-ink checked:before:content-['✔'] checked:before:text-canvas checked:before:block checked:before:text-center checked:before:text-xs"
                                />
                            </div>
                            <label htmlFor="rgpd" className="text-xs text-ink/80 leading-tight cursor-pointer select-none font-medium">
                                Acepto el tratamiento de mis datos de salud conforme al RGPD y la política de{' '}
                                <Link href="/privacidad" target="_blank" className="text-ink font-bold hover:underline">
                                    Privacidad
                                </Link>.
                            </label>
                        </div>

                        {/* Checkbox Marketing (opcional, nunca bloquea el envío) */}
                        <div className="flex items-start gap-3 mt-3">
                            <div className="flex items-center h-5">
                                <input
                                    id="marketing"
                                    type="checkbox"
                                    checked={consentimientoMarketing}
                                    onChange={(e) => setConsentimientoMarketing(e.target.checked)}
                                    className="w-5 h-5 text-ink bg-surface-2 border-2 border-ink rounded focus:ring-ink focus:ring-2 cursor-pointer appearance-none checked:bg-ink checked:before:content-['✔'] checked:before:text-canvas checked:before:block checked:before:text-center checked:before:text-xs"
                                />
                            </div>
                            <label htmlFor="marketing" className="text-xs text-ink/80 leading-tight cursor-pointer select-none font-medium">
                                (Opcional) Quiero recibir consejos, novedades y ofertas de TherapyRecap por email. Puedo darme de baja cuando quiera.
                            </label>
                        </div>

                        {/* Botón Submit */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-ink text-canvas border-2 border-ink hover:bg-ink/90 font-bold py-3 rounded-xl shadow-[4px_4px_0px_0px_var(--shadow-color)] hover:translate-y-0.5 hover:shadow-[2px_2px_0px_0px_var(--shadow-color)] active:translate-y-1 active:shadow-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center"
                        >
                            {isLoading ? (
                                <span className="material-symbols-outlined animate-spin text-xl" aria-hidden="true">
                                    progress_activity
                                </span>
                            ) : (
                                'Crear Cuenta'
                            )}
                        </button>
                    </form>

                    {/* Separador */}
                    <div className="relative my-8">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t-2 border-ink/20"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-4 bg-surface-2 text-ink/60 font-medium">
                                O continúa con
                            </span>
                        </div>
                    </div>

                    {/* Botón Google */}
                    <button
                        type="button"
                        onClick={() => handleGoogleLogin()}
                        disabled={isLoading}
                        className="w-full flex items-center justify-center gap-3 bg-surface-2 hover:bg-tint-neutral border-2 border-ink text-ink font-bold py-3 rounded-xl shadow-[4px_4px_0px_0px_var(--shadow-color)] hover:translate-y-0.5 hover:shadow-[2px_2px_0px_0px_var(--shadow-color)] active:translate-y-1 active:shadow-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                fill="#4285F4"
                            />
                            <path
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                fill="#34A853"
                            />
                            <path
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                fill="#FBBC05"
                            />
                            <path
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                fill="#EA4335"
                            />
                        </svg>
                        Google
                    </button>

                    {/* Footer Link */}
                    <div className="mt-8 text-center text-sm text-ink/80 font-medium">
                        ¿Ya tienes cuenta?{' '}
                        <Link href="/app/login" className="text-ink font-bold hover:underline transition-all">
                            Inicia sesión
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
